import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';

const ReputationProfile = () => {
  const navigate = useNavigate();
  const [reputation, setReputation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [records, setRecords] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const perPage = 10;

  useEffect(() => {
    fetchReputation();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [page]);

  // 监听声望更新事件
  useEffect(() => {
    const handleReputationUpdate = () => {
      fetchReputation();
    };

    window.addEventListener('reputationUpdate', handleReputationUpdate);

    return () => {
      window.removeEventListener('reputationUpdate', handleReputationUpdate);
    };
  }, []);

  const fetchReputation = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/jifen/reputation', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReputation(response.data);
    } catch (err) {
      setError('获取声望信息失败');
      console.error('获取声望信息失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/jifen/reputation/history', {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, per_page: perPage }
      });

      setRecords(response.data.data);
      setTotal(response.data.total);
    } catch (err) {
      console.error('获取声望流水失败:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      1: 'from-orange-300 to-orange-400',
      2: 'from-red-300 to-red-400',
      3: 'from-pink-300 to-pink-400',
      4: 'from-indigo-300 to-indigo-400'
    };
    return colors[level] || 'from-orange-300 to-orange-400';
  };

  const getLevelProgress = () => {
    if (!reputation) return 0;
    const current = reputation.current_score;
    const next = reputation.next_level_score || (reputation.level * 500);
    const prev = reputation.level === 1 ? 0 : (reputation.level - 1) * 500;
    return ((current - prev) / (next - prev)) * 100;
  };

  const getChangeTypeText = (type) => {
    const typeMap = {
      1: '发帖',
      2: '回帖',
      3: '内容被点赞',
      4: '取消点赞',
      5: '内容被收藏',
      6: '取消收藏',
      7: '违规扣减',
      8: '获得赏金',
      9: '内容被分享',
      10: '优质回答',
      11: '举报成功',
      12: '活动参与'
    };
    return typeMap[type] || '未知';
  };

  const getTypeIcon = (type) => {
    if ([1, 2, 8].includes(type)) {
      return '📝'; // 内容贡献
    } else if ([3, 4, 7].includes(type)) {
      return '👍'; // 社区互动
    } else if (type === 6) {
      return '💰'; // 赏金
    } else if (type === 9) {
      return '🚨'; // 举报
    } else if (type === 5) {
      return '⚠️'; // 违规
    } else {
      return '🎯'; // 其他
    }
  };

  const getTypeColor = (type) => {
    if (type === 5) {
      return 'text-red-600 bg-red-100';
    } else {
      return 'text-green-600 bg-green-100';
    }
  };

  const formatScore = (score) => {
    return score > 0 ? `+${score}` : score.toString();
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(new Date(value));
  };

  const totalPages = Math.ceil(total / perPage);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="返回上一页"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">声望信息</h2>
      </div>

      {/* 声望等级卡片 */}
      <div className={`bg-gradient-to-r ${getLevelColor(reputation?.level)} rounded-2xl p-4 sm:p-6 text-white mb-6`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-semibold truncate">
              当前等级：{reputation?.level_name}（Lv.{reputation?.level}）
            </p>
          </div>
        </div>

        {/* 等级进度条 */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs sm:text-sm mb-1">
            <span>等级进度</span>
            <span>{reputation?.next_level_score ? `${reputation.current_score}/${reputation.next_level_score}` : '已满级'}</span>
          </div>
          <div className="w-full bg-white bg-opacity-50 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${getLevelProgress()}%` }}
            ></div>
          </div>
        </div>

        {/* 统计信息 */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="bg-white bg-opacity-20 rounded-lg p-2.5 sm:p-3 text-center">
            <div className="text-base sm:text-lg font-bold leading-none">{reputation?.current_score || 0}</div>
            <div className="text-[11px] sm:text-xs opacity-90 mt-1">声望余额</div>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-2.5 sm:p-3 text-center">
            <div className="text-base sm:text-lg font-bold leading-none">{reputation?.highest_score || 0}</div>
            <div className="text-[11px] sm:text-xs opacity-90 mt-1">最高声望</div>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-2.5 sm:p-3 text-center">
            <div className="text-base sm:text-lg font-bold leading-none">{reputation?.highest_level || reputation?.level || 1}</div>
            <div className="text-[11px] sm:text-xs opacity-90 mt-1">最高等级</div>
          </div>
        </div>
      </div>

      {/* 声望历史 */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">声望历史</h3>

        {historyLoading && page === 1 ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {/* 流水列表 */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-hidden">
                <table className="w-full table-fixed divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-[34%] px-3 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                        时间
                      </th>
                      <th className="w-[18%] px-3 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                        类型
                      </th>
                      <th className="w-[14%] px-3 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                        变动
                      </th>
                      <th className="w-[14%] px-3 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                        余额
                      </th>
                      <th className="w-[20%] px-3 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                        描述
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {records.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-3 py-4 text-center text-gray-500">
                          暂无声望历史记录
                        </td>
                      </tr>
                    ) : (
                      records.map((record) => (
                        <tr key={record.record_id} className="hover:bg-gray-50 transition duration-150">
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 truncate">
                            {formatDateTime(record.created_time)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 truncate">
                            {getChangeTypeText(record.change_type)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                            <span className={record.change_score > 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatScore(record.change_score)}
                            </span>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                            {record.balance_score}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-900 truncate">
                            {record.remark || record.related_chart_type || '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  第 {page} 页，共 {totalPages} 页
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 声望说明 */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-100 transition duration-200"
        >
          <h3 className="text-lg font-medium text-gray-800">声望说明</h3>
          <svg
            className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 border-t border-gray-200">
            {/* 声望等级说明 */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-blue-800 mb-3">声望等级说明</h4>
              <div className="space-y-2 text-sm text-blue-700">
                <div className="flex justify-between">
                  <span>萌新 (Lv.1)</span>
                  <span>0-100 声望</span>
                </div>
                <div className="flex justify-between">
                  <span>进阶 (Lv.2)</span>
                  <span>101-500 声望</span>
                </div>
                <div className="flex justify-between">
                  <span>资深 (Lv.3)</span>
                  <span>501-2000 声望</span>
                </div>
                <div className="flex justify-between">
                  <span>专家 (Lv.4)</span>
                  <span>2001+ 声望</span>
                </div>
              </div>
            </div>

            {/* 获得声望的方式 */}
            <div>
              <h4 className="text-md font-medium text-green-800 mb-3">获得声望的方式</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-green-700">
                <div>
                  <h5 className="font-medium mb-2">内容贡献</h5>
                  <ul className="space-y-1">
                    <li>• 发帖: +5 声望</li>
                    <li>• 回帖: +2 声望</li>
                    <li>• 优质回答: +10 声望</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">社区互动</h5>
                  <ul className="space-y-1">
                    <li>• 内容被点赞: +1/次</li>
                    <li>• 内容被收藏: +2/次</li>
                    <li>• 获得赏金: +3 声望</li>
                    <li>• 举报成功: +5 声望</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReputationProfile;